// The properties bar: everything that acts on the CURRENT selection, or
// on the armed tool when nothing is selected. One rule decides what you
// see — "the selection's properties, else the tool's" — and each cluster
// below reads the selected object first and falls back to the tool's
// stored default, so the same control edits an existing object and sets
// the next one's style.
//
// These used to be eight conditional blocks inlined into the toolbar
// row, which meant the toolbar's width changed as you worked and buttons
// moved under the cursor. Now the same clusters render in two shapes
// from one source: `layout="bar"` is the horizontal strip the phone
// shell puts along the bottom, and `layout="panel"` is the labelled,
// wrapping column the desktop stacks above the right-hand drawer (see
// `OptionsPanel` at the foot of this file).
import type { HTMLAttributes, ReactNode, RefObject } from "react";
import { toolMeta } from "../lib/commands.ts";
import {
  HIGHLIGHT_COLORS,
  MARKUP_COLORS,
  NOTE_COLORS,
} from "../lib/color-utils.ts";
import { FONT_LIBRARY, type CustomFont } from "../lib/fonts.ts";
import { CJK_SCRIPTS, hasCjk, type CjkScript } from "../lib/cjk.ts";
import {
  isMarkup,
  shapeHeads,
  type Annotation,
  type DashStyle,
  type HeadStyle,
  type InkAnnotation,
  type MarkupKind,
  type NoteAnnotation,
  type ShapeAnnotation,
} from "../lib/protocol.ts";
import type { AlignMode } from "../lib/snap.ts";
import {
  isShapeTool,
  type ActiveTextEdit,
  type AnnotStylePatch,
  type EditFamily,
  type Tool,
} from "../lib/ui-types.ts";
import { ColorPicker } from "./ColorPicker.tsx";

/** "bar" — one horizontal, sideways-scrolling strip (the phone shell).
 *  "panel" — a labelled column that wraps (the desktop right pane). */
export type PropsBarLayout = "bar" | "panel";

export interface PropsBarProps {
  tool: Tool;
  /** The selected annotation, when exactly one is selected. */
  selectedAnnot: Annotation | null;
  selectedInk: InkAnnotation | null;
  selectedShape: ShapeAnnotation | null;
  selectedNote: NoteAnnotation | null;
  /** The markup kind whose colours to offer — from the tool or the
   *  selection, whichever is live. */
  activeMarkupKind: MarkupKind | null;
  /** Patch the selected annotation (no-op when nothing is selected). */
  updateSelected: (patch: AnnotStylePatch) => void;

  highlightColor: string;
  setHighlightColor: (c: string) => void;
  markupColor: string;
  setMarkupColor: (c: string) => void;

  textSize: number;
  setTextSize: (n: number) => void;
  /** Text-box typeface (see TextAnnotation.font). */
  textFont: string;
  setTextFont: (id: string) => void;
  /** Which CJK ordering Han ideographs use — only offered once the text
   *  actually contains some. */
  textCjkScript: CjkScript;
  setTextCjkScript: (s: CjkScript) => void;
  textBold: boolean;
  setTextBold: (v: boolean) => void;
  textItalic: boolean;
  setTextItalic: (v: boolean) => void;
  /** Open the "add a Google font" dialog. */
  onOpenGoogleFont: () => void;
  textColor: string;
  setTextColor: (c: string) => void;
  textAlign: "left" | "center" | "right";
  setTextAlign: (a: "left" | "center" | "right") => void;
  textOpacity: number;
  setTextOpacity: (n: number) => void;

  inkWidth: number;
  setInkWidth: (n: number) => void;
  inkColor: string;
  setInkColor: (c: string) => void;

  shapeWidth: number;
  setShapeWidth: (n: number) => void;
  shapeColor: string;
  setShapeColor: (c: string) => void;
  shapeDash: DashStyle;
  setShapeDash: (d: DashStyle) => void;
  shapeHeadStart: HeadStyle;
  setShapeHeadStart: (h: HeadStyle) => void;
  shapeHeadEnd: HeadStyle;
  setShapeHeadEnd: (h: HeadStyle) => void;
  shapeFillOn: boolean;
  setShapeFillOn: (v: boolean) => void;
  shapeFillColor: string;
  setShapeFillColor: (c: string) => void;

  /** The open content-text edit, if any — its own formatting cluster. */
  textEdit?: ActiveTextEdit;
  patchTextEdit: (patch: Partial<ActiveTextEdit>) => void;
  customFonts: CustomFont[];
  fontUploadInputRef: RefObject<HTMLInputElement | null>;

  noteColor: string;
  setNoteColor: (c: string) => void;

  measureUnit: "pt" | "mm" | "cm" | "in";
  setMeasureUnit: (u: "pt" | "mm" | "cm" | "in") => void;
  calibrateMeasure: () => void;
  measureFactor: number;
  setMeasureFactor: (n: number) => void;

  /** How many objects are selected — align/distribute need two/three. */
  selCount: number;
  applyAlign: (mode: AlignMode) => void;
  applyDistribute: (axis: "h" | "v") => void;
}

/** One cluster of controls. In "bar" it is an unlabelled inline run; in
 *  "panel" it becomes a titled block that wraps, because a 288-px pane
 *  cannot hold five selects side by side and an unlabelled column of
 *  loose widgets reads as noise. Extra props (notably
 *  `data-pe-format-bar`, which PageView's blur guard looks for with
 *  `closest`) land on the element that actually wraps the controls in
 *  both layouts. */
function Group({
  layout,
  label,
  barClass = "ml-1 flex items-center gap-1.5 text-sm",
  children,
  ...rest
}: {
  layout: PropsBarLayout;
  label: string;
  barClass?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  if (layout === "bar") {
    return (
      <div {...rest} className={barClass}>
        {children}
      </div>
    );
  }
  return (
    <section className="border-b border-[color:var(--color-border)] px-3 py-2.5 last:border-b-0">
      <h3 className="pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-fg-muted)]">
        {label}
      </h3>
      <div {...rest} className="flex flex-wrap items-center gap-1.5 text-sm">
        {children}
      </div>
    </section>
  );
}

export function PropsBar({
  layout = "bar",
  tool,
  selectedAnnot,
  selectedInk,
  selectedShape,
  selectedNote,
  activeMarkupKind,
  updateSelected,
  highlightColor,
  setHighlightColor,
  markupColor,
  setMarkupColor,
  textSize,
  setTextSize,
  textFont,
  setTextFont,
  textCjkScript,
  setTextCjkScript,
  textBold,
  setTextBold,
  textItalic,
  setTextItalic,
  onOpenGoogleFont,
  textColor,
  setTextColor,
  textAlign,
  setTextAlign,
  textOpacity,
  setTextOpacity,
  inkWidth,
  setInkWidth,
  inkColor,
  setInkColor,
  shapeWidth,
  setShapeWidth,
  shapeColor,
  setShapeColor,
  shapeDash,
  setShapeDash,
  shapeHeadStart,
  setShapeHeadStart,
  shapeHeadEnd,
  setShapeHeadEnd,
  shapeFillOn,
  setShapeFillOn,
  shapeFillColor,
  setShapeFillColor,
  textEdit,
  patchTextEdit,
  customFonts,
  fontUploadInputRef,
  noteColor,
  setNoteColor,
  measureUnit,
  setMeasureUnit,
  calibrateMeasure,
  measureFactor,
  setMeasureFactor,
  selCount,
  applyAlign,
  applyDistribute,
}: PropsBarProps & { layout?: PropsBarLayout }) {
  return (
    <>
        {/* contextual: markup colour (highlight gets pastels, the line
            markups get the saturated set) */}
        {activeMarkupKind && (
          <Group
            layout={layout}
            label={activeMarkupKind === "highlight" ? "Highlight colour" : "Markup colour"}
            barClass="ml-1 flex items-center gap-1"
          >
            {(activeMarkupKind === "highlight" ? HIGHLIGHT_COLORS : MARKUP_COLORS).map((c) => {
              const current =
                selectedAnnot && isMarkup(selectedAnnot)
                  ? selectedAnnot.color
                  : activeMarkupKind === "highlight"
                    ? highlightColor
                    : markupColor;
              return (
                <button
                  key={c}
                  type="button"
                  title={`Colour ${c}`}
                  onClick={() => {
                    if (activeMarkupKind === "highlight") setHighlightColor(c);
                    else setMarkupColor(c);
                    if (selectedAnnot && isMarkup(selectedAnnot)) updateSelected({ color: c });
                  }}
                  className={`h-5 w-5 rounded-full border ${
                    c === current
                      ? "border-[color:var(--color-fg)] ring-1 ring-[color:var(--color-fg)]"
                      : "border-[color:var(--color-border-strong)]"
                  }`}
                  style={{ background: c }}
                />
              );
            })}
            <ColorPicker
              value={
                selectedAnnot && isMarkup(selectedAnnot)
                  ? selectedAnnot.color
                  : activeMarkupKind === "highlight"
                    ? highlightColor
                    : markupColor
              }
              onChange={(hex) => {
                if (activeMarkupKind === "highlight") setHighlightColor(hex);
                else setMarkupColor(hex);
                if (selectedAnnot && isMarkup(selectedAnnot)) updateSelected({ color: hex });
              }}
              title="Custom colour"
            />
          </Group>
        )}

        {/* contextual: text properties */}
        {(tool === "text" || selectedAnnot?.kind === "text") && (
          <Group layout={layout} label="Text">
            <select
              value={
                selectedAnnot?.kind === "text" ? (selectedAnnot.font ?? "helv") : textFont
              }
              onChange={(e) => {
                const font = e.target.value;
                setTextFont(font);
                if (selectedAnnot?.kind === "text") updateSelected({ font });
              }}
              className="h-8 max-w-36 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
              title="Font family"
            >
              <optgroup label="Standard">
                <option value="helv">Helvetica</option>
                <option value="times">Times</option>
                <option value="cour">Courier</option>
              </optgroup>
              <optgroup label="Library">
                {FONT_LIBRARY.map((f) => (
                  <option key={f.id} value={`lib:${f.id}`}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
              {customFonts.length > 0 && (
                <optgroup label="Added this session">
                  {customFonts.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              title="Use your own font file (TTF/OTF) — embedded into the PDF"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => fontUploadInputRef.current?.click()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-upload-2-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Add a font from Google Fonts"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onOpenGoogleFont}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-google-line" aria-hidden />
            </button>
            {/* Only shown when it can matter: which ordering to draw Han
                with is a real choice (the same ideographs are written in
                all four scripts), but it is noise for anyone typing
                Latin. Kana or hangul in the text answers the question
                on its own, so the picker is for Han-only text. */}
            {(selectedAnnot?.kind === "text"
              ? hasCjk(selectedAnnot.value)
              : tool === "text") && (
              <select
                value={
                  selectedAnnot?.kind === "text"
                    ? (selectedAnnot.cjkScript ?? "zh-Hans")
                    : textCjkScript
                }
                onChange={(e) => {
                  const cjkScript = e.target.value as CjkScript;
                  setTextCjkScript(cjkScript);
                  if (selectedAnnot?.kind === "text") updateSelected({ cjkScript });
                }}
                className="h-8 max-w-32 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
                title="Which CJK script to draw Han characters with"
              >
                {CJK_SCRIPTS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}
            <input
              type="number"
              min={6}
              max={96}
              value={selectedAnnot?.kind === "text" ? selectedAnnot.size : textSize}
              onChange={(e) => {
                const size = Math.min(96, Math.max(6, Number(e.target.value) || 14));
                setTextSize(size);
                if (selectedAnnot?.kind === "text") updateSelected({ size });
              }}
              className="h-8 w-14 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5"
              title="Font size (pt)"
            />
            <div className="flex overflow-hidden rounded-md border border-[color:var(--color-border)]">
              {(
                [
                  ["bold", "B", "Bold", "font-bold"],
                  ["italic", "I", "Italic", "italic"],
                ] as const
              ).map(([which, glyph, label, cls]) => {
                const on =
                  selectedAnnot?.kind === "text"
                    ? which === "bold"
                      ? !!selectedAnnot.bold
                      : !!selectedAnnot.italic
                    : which === "bold"
                      ? textBold
                      : textItalic;
                return (
                  <button
                    key={which}
                    type="button"
                    title={label}
                    aria-pressed={on}
                    onClick={() => {
                      const next = !on;
                      if (which === "bold") setTextBold(next);
                      else setTextItalic(next);
                      if (selectedAnnot?.kind === "text") {
                        updateSelected(which === "bold" ? { bold: next } : { italic: next });
                      }
                    }}
                    className={`inline-flex h-8 w-8 items-center justify-center text-sm ${cls} ${
                      on
                        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                        : "hover:bg-[color:var(--color-bg-subtle)]"
                    }`}
                  >
                    {glyph}
                  </button>
                );
              })}
            </div>
            <ColorPicker
              value={selectedAnnot?.kind === "text" ? selectedAnnot.color : textColor}
              onChange={(hex) => {
                setTextColor(hex);
                if (selectedAnnot?.kind === "text") updateSelected({ color: hex });
              }}
              title="Text colour"
            />
            <div className="flex overflow-hidden rounded-md border border-[color:var(--color-border)]">
              {(
                [
                  ["left", "ri-align-left", "Align left"],
                  ["center", "ri-align-center", "Align centre"],
                  ["right", "ri-align-right", "Align right"],
                ] as const
              ).map(([mode, icon, label]) => {
                const current =
                  selectedAnnot?.kind === "text" ? (selectedAnnot.align ?? "left") : textAlign;
                return (
                  <button
                    key={mode}
                    type="button"
                    title={label}
                    aria-pressed={current === mode}
                    onClick={() => {
                      setTextAlign(mode);
                      if (selectedAnnot?.kind === "text") updateSelected({ align: mode });
                    }}
                    className={`inline-flex h-8 w-7 items-center justify-center ${
                      current === mode
                        ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                        : "hover:bg-[color:var(--color-bg-subtle)]"
                    }`}
                  >
                    <i className={icon} aria-hidden />
                  </button>
                );
              })}
            </div>
            <input
              type="range"
              min={20}
              max={100}
              title="Text opacity"
              value={Math.round(
                (selectedAnnot?.kind === "text" ? (selectedAnnot.opacity ?? 1) : textOpacity) * 100
              )}
              onChange={(e) => {
                const opacity = Number(e.target.value) / 100;
                setTextOpacity(opacity);
                if (selectedAnnot?.kind === "text") updateSelected({ opacity });
              }}
              className="w-16"
            />
          </Group>
        )}

        {/* contextual: ink properties */}
        {(tool === "ink" || selectedInk) && (
          <Group layout={layout} label="Freehand">
            <input
              type="number"
              min={1}
              max={12}
              value={selectedInk?.strokeWidth ?? inkWidth}
              onChange={(e) => {
                const strokeWidth = Math.min(12, Math.max(1, Number(e.target.value) || 2));
                setInkWidth(strokeWidth);
                if (selectedInk) updateSelected({ strokeWidth });
              }}
              className="h-8 w-14 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5"
              title="Stroke width (pt)"
            />
            <ColorPicker
              value={selectedInk?.color ?? inkColor}
              onChange={(hex) => {
                setInkColor(hex);
                if (selectedInk) updateSelected({ color: hex });
              }}
              title="Stroke colour"
            />
          </Group>
        )}

        {/* contextual: shape properties */}
        {(isShapeTool(tool) || selectedShape) && (
          <Group layout={layout} label="Shape">
            <input
              type="number"
              min={1}
              max={12}
              value={selectedShape?.strokeWidth ?? shapeWidth}
              onChange={(e) => {
                const strokeWidth = Math.min(12, Math.max(1, Number(e.target.value) || 2));
                setShapeWidth(strokeWidth);
                if (selectedShape) updateSelected({ strokeWidth });
              }}
              className="h-8 w-14 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5"
              title="Stroke width (pt)"
            />
            <ColorPicker
              value={selectedShape?.color ?? shapeColor}
              onChange={(hex) => {
                setShapeColor(hex);
                if (selectedShape) updateSelected({ color: hex });
              }}
              title="Stroke colour"
            />
            <select
              value={selectedShape ? (selectedShape.dash ?? "solid") : shapeDash}
              onChange={(e) => {
                const dash = e.target.value as DashStyle;
                setShapeDash(dash);
                if (selectedShape) updateSelected({ dash });
              }}
              className="h-8 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
              title="Line style"
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </select>
            {(selectedShape
              ? selectedShape.shape === "line" || selectedShape.shape === "arrow"
              : tool === "line" || tool === "arrow") && (
              <>
                {(
                  [
                    ["headStart", "Start", selectedShape ? shapeHeads(selectedShape).start : shapeHeadStart],
                    ["headEnd", "End", selectedShape ? shapeHeads(selectedShape).end : shapeHeadEnd],
                  ] as const
                ).map(([field, label, current]) => (
                  <select
                    key={field}
                    value={current}
                    onChange={(e) => {
                      const v = e.target.value as HeadStyle;
                      if (field === "headStart") setShapeHeadStart(v);
                      else setShapeHeadEnd(v);
                      if (selectedShape) updateSelected({ [field]: v });
                    }}
                    className="h-8 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
                    title={`${label} of line`}
                  >
                    <option value="none">{label}: none</option>
                    <option value="arrow">{label}: arrow</option>
                    <option value="open">{label}: open ›</option>
                    <option value="circle">{label}: circle</option>
                    <option value="square">{label}: square</option>
                    <option value="diamond">{label}: diamond</option>
                  </select>
                ))}
              </>
            )}
            {(selectedShape
              ? selectedShape.shape === "rect" || selectedShape.shape === "ellipse"
              : tool === "rect" || tool === "ellipse") && (
              <>
                <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-[color:var(--color-fg-muted)]">
                  <input
                    type="checkbox"
                    checked={selectedShape ? selectedShape.fill != null : shapeFillOn}
                    onChange={(e) => {
                      setShapeFillOn(e.target.checked);
                      if (selectedShape) {
                        updateSelected({ fill: e.target.checked ? shapeFillColor : null });
                      }
                    }}
                  />
                  Fill
                </label>
                {(selectedShape ? selectedShape.fill != null : shapeFillOn) && (
                  <ColorPicker
                    value={selectedShape?.fill ?? shapeFillColor}
                    onChange={(hex) => {
                      setShapeFillColor(hex);
                      if (selectedShape) updateSelected({ fill: hex });
                    }}
                    title="Fill colour"
                  />
                )}
              </>
            )}
          </Group>
        )}

        {/* contextual: content-text formatting (while an edit is open) */}
        {textEdit && (
          <Group layout={layout} label="Formatting" data-pe-format-bar>
            <select
              value={textEdit.family}
              onChange={(e) => patchTextEdit({ family: e.target.value as EditFamily })}
              className="h-8 max-w-40 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
              title="Font family"
            >
              <option value="original">Original font</option>
              <optgroup label="Built-in">
                <option value="helv">Helvetica</option>
                <option value="times">Times</option>
                <option value="cour">Courier</option>
              </optgroup>
              <optgroup label="Library">
                {FONT_LIBRARY.map((f) => (
                  <option key={f.id} value={`lib:${f.id}`}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
              {customFonts.length > 0 && (
                <optgroup label="Uploaded">
                  {customFonts.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              title="Use your own font (TTF/OTF) — embedded into the PDF"
              // The OS file dialog steals focus; without this the edit
              // blurs and commits before the font is even chosen.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => fontUploadInputRef.current?.click()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-upload-2-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Add a font from Google Fonts"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onOpenGoogleFont}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-google-line" aria-hidden />
            </button>
            <input
              type="number"
              min={4}
              max={144}
              value={Math.round(textEdit.size * 10) / 10}
              onChange={(e) =>
                patchTextEdit({ size: Math.min(144, Math.max(4, Number(e.target.value) || 12)) })
              }
              className="h-8 w-16 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5"
              title="Font size (pt)"
            />
            <button
              type="button"
              title="Bold"
              aria-pressed={textEdit.bold}
              onClick={() => patchTextEdit({ bold: !textEdit.bold })}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md font-bold ${
                textEdit.bold
                  ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                  : "hover:bg-[color:var(--color-bg-subtle)]"
              }`}
            >
              B
            </button>
            <button
              type="button"
              title="Italic"
              aria-pressed={textEdit.italic}
              onClick={() => patchTextEdit({ italic: !textEdit.italic })}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md italic ${
                textEdit.italic
                  ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                  : "hover:bg-[color:var(--color-bg-subtle)]"
              }`}
            >
              I
            </button>
            <ColorPicker
              value={textEdit.color}
              onChange={(hex) => patchTextEdit({ color: hex })}
              title="Text colour"
            />
            <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-[color:var(--color-fg-muted)]">
              <input
                type="checkbox"
                checked={textEdit.background !== null}
                onChange={(e) =>
                  patchTextEdit({ background: e.target.checked ? "#ffe066" : null })
                }
              />
              Bg
            </label>
            {textEdit.background !== null && (
              <ColorPicker
                value={textEdit.background}
                onChange={(hex) => patchTextEdit({ background: hex })}
                title="Background colour"
              />
            )}
          </Group>
        )}

        {/* contextual: note colour */}
        {(tool === "note" || selectedNote) && (
          <Group layout={layout} label="Sticky note" barClass="ml-1 flex items-center gap-1">
            {NOTE_COLORS.map((c) => {
              const current = selectedNote?.color ?? noteColor;
              return (
                <button
                  key={c}
                  type="button"
                  title={`Note colour ${c}`}
                  onClick={() => {
                    setNoteColor(c);
                    if (selectedNote) updateSelected({ color: c });
                  }}
                  className={`h-5 w-5 rounded-full border ${
                    c === current
                      ? "border-[color:var(--color-fg)] ring-1 ring-[color:var(--color-fg)]"
                      : "border-[color:var(--color-border-strong)]"
                  }`}
                  style={{ background: c }}
                />
              );
            })}
            <ColorPicker
              value={selectedNote?.color ?? noteColor}
              onChange={(hex) => {
                setNoteColor(hex);
                if (selectedNote) updateSelected({ color: hex });
              }}
              title="Custom colour"
            />
          </Group>
        )}

        {/* contextual: measure units + calibration */}
        {tool === "measure" && (
          <Group layout={layout} label="Measure">
            <select
              value={measureUnit}
              onChange={(e) => setMeasureUnit(e.target.value as "pt" | "mm" | "cm" | "in")}
              className="h-8 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1 text-sm"
              title="Measurement unit"
            >
              <option value="mm">mm</option>
              <option value="cm">cm</option>
              <option value="in">in</option>
              <option value="pt">pt</option>
            </select>
            <button
              type="button"
              title="Calibrate: tell the editor the real length of your last measurement (for drawings at scale)"
              onClick={calibrateMeasure}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-[color:var(--color-border)] px-2 text-xs hover:bg-[color:var(--color-bg-subtle)]"
            >
              <i className="ri-ruler-2-line" aria-hidden /> Calibrate
            </button>
            {measureFactor !== 1 && (
              <button
                type="button"
                title="Calibrated scale — click to reset to 1:1"
                onClick={() => setMeasureFactor(1)}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-[color:var(--color-accent)] px-2 text-xs text-[color:var(--color-accent)] hover:bg-[color:var(--color-bg-subtle)]"
              >
                ×{measureFactor.toFixed(3)} <i className="ri-close-line" aria-hidden />
              </button>
            )}
          </Group>
        )}

        {/* contextual: align / distribute (any multi-selection —
            annotations, page content and form fields alike) */}
        {selCount >= 2 && (
          <Group layout={layout} label="Arrange" barClass="ml-1 flex items-center gap-0.5">
            {(
              [
                ["left", "ri-align-left", "Align left edges"],
                ["centerH", "ri-align-center", "Align horizontal centres"],
                ["right", "ri-align-right", "Align right edges"],
                ["top", "ri-align-top", "Align top edges"],
                ["middle", "ri-align-vertically", "Align vertical centres"],
                ["bottom", "ri-align-bottom", "Align bottom edges"],
              ] as const
            ).map(([mode, icon, label]) => (
              <button
                key={mode}
                type="button"
                title={label}
                onClick={() => applyAlign(mode)}
                className="inline-flex h-8 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)]"
              >
                <i className={icon} aria-hidden />
              </button>
            ))}
            <button
              type="button"
              title="Distribute horizontally (equal gaps)"
              disabled={selCount < 3}
              onClick={() => applyDistribute("h")}
              className="inline-flex h-8 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-arrow-left-right-line" aria-hidden />
            </button>
            <button
              type="button"
              title="Distribute vertically (equal gaps)"
              disabled={selCount < 3}
              onClick={() => applyDistribute("v")}
              className="inline-flex h-8 w-7 items-center justify-center rounded-md hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
            >
              <i className="ri-arrow-up-down-line" aria-hidden />
            </button>
            <span className="ml-1 text-xs tabular-nums text-[color:var(--color-fg-muted)]">
              {selCount} selected
            </span>
          </Group>
        )}
    </>
  );
}

/** Does the bar have anything to show right now? The row is hidden
 *  entirely when not — on a phone that is most of the time, and an empty
 *  56-px strip along the bottom is 56 px of document you can't see. */
export function propsBarHasContent(p: {
  tool: Tool;
  selectedAnnot: Annotation | null;
  selectedInk: InkAnnotation | null;
  selectedShape: ShapeAnnotation | null;
  selectedNote: NoteAnnotation | null;
  activeMarkupKind: MarkupKind | null;
  textEdit?: ActiveTextEdit;
  selCount: number;
}): boolean {
  return Boolean(
    p.activeMarkupKind ||
      p.tool === "text" ||
      p.selectedAnnot?.kind === "text" ||
      p.tool === "ink" ||
      p.selectedInk ||
      isShapeTool(p.tool) ||
      p.selectedShape ||
      p.textEdit ||
      p.tool === "note" ||
      p.selectedNote ||
      p.tool === "measure" ||
      p.selCount >= 2
  );
}

/** What the pane header calls the current context. The selection wins
 *  over the armed tool for the same reason the controls do — when you
 *  have something selected, these knobs edit THAT. */
function optionsSubtitle(p: PropsBarProps): string {
  if (p.textEdit) return "Editing page text";
  if (p.selCount >= 2) return `${p.selCount} objects selected`;
  if (p.selectedAnnot) return "Selection";
  return toolMeta(p.tool).label;
}

/** The armed tool's (or the selection's) options as a column above the
 *  right-hand panel drawer. This used to be a full-width strip under the
 *  toolbar; a document is taller than it is wide, so the vertical edge is
 *  the cheaper place to spend the space — and the controls stop sitting
 *  between the toolbar and the page you are pointing at.
 *
 *  `capped` limits it to just over half the column so the drawer below
 *  keeps a usable height; on its own it takes the whole column. */
export function OptionsPanel({
  capped,
  onHidePane,
  ...props
}: PropsBarProps & {
  capped: boolean;
  /** Hide the whole right column. Lives here because this section is
   *  always the top of that column, so its header is the one place a
   *  close button is in the same spot every time. */
  onHidePane?: () => void;
}) {
  // The section is always rendered so the column keeps its width; when
  // the armed tool has nothing to configure it says so. A sentence is a
  // much smaller interruption than the document reflowing.
  const hasContent = propsBarHasContent(props);
  return (
    <section
      aria-label="Tool options"
      className={
        "flex min-h-0 flex-col border-b border-[color:var(--color-border)] " +
        (capped ? "max-h-[55%] shrink-0" : hasContent ? "flex-1" : "shrink-0")
      }
    >
      <header className="flex h-9 shrink-0 items-center gap-1.5 border-b border-[color:var(--color-border)] px-2 text-xs font-semibold">
        <i className="ri-sliders-3-line" aria-hidden />
        Options
        <span className="ml-auto min-w-0 truncate pl-2 font-normal text-[color:var(--color-fg-muted)]">
          {optionsSubtitle(props)}
        </span>
        {onHidePane && (
          <button
            type="button"
            title="Hide this panel"
            aria-label="Hide side panel"
            onClick={onHidePane}
            className="-mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-layout-right-line" aria-hidden />
          </button>
        )}
      </header>
      {hasContent ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PropsBar {...props} layout="panel" />
        </div>
      ) : (
        <p className="px-2 py-2 text-[11px] leading-relaxed text-[color:var(--color-fg-muted)]">
          Nothing to set for this tool. Pick a drawing tool, or select something, and its options
          appear here.
        </p>
      )}
    </section>
  );
}
